import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../App';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Compass, Search, CalendarDays, X } from 'lucide-react';
import { toast } from 'sonner';
import SessionCard from '../components/sessions/SessionCard';
import RequestJoinDialog from '../components/sessions/RequestJoinDialog';
import { apiError } from '../lib/apiError';

const SUBJECTS = ['Physics', 'Chemistry', 'Mathematics', 'Biology'];
const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const Empty = ({ text, testId }) => (
  <div className="py-16 text-center rounded-2xl border border-dashed border-slate-300 bg-white" data-testid={testId}>
    <Compass className="w-10 h-10 text-slate-300 mx-auto mb-3" />
    <p className="text-slate-500">{text}</p>
  </div>
);

const DiscoverSessions = () => {
  const [sessions, setSessions] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [subject, setSubject] = useState('all');
  const [date, setDate] = useState(null);
  const [target, setTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (subject !== 'all') params.subject = subject;
      if (date) params.date = toIso(date);
      const [s, r] = await Promise.all([
        axios.get(`${API}/sessions/discover`, { params }),
        axios.get(`${API}/sessions/my-requests`),
      ]);
      setSessions(s.data || []);
      setRequests(r.data || []);
    } catch (e) {
      toast.error(apiError(e, 'Failed to load sessions'));
    } finally {
      setLoading(false);
    }
  }, [q, subject, date]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const withdraw = async (requestId) => {
    try {
      await axios.post(`${API}/sessions/join-requests/${requestId}/withdraw`);
      toast.success('Request withdrawn');
      load();
    } catch (e) {
      toast.error(apiError(e, 'Could not withdraw'));
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="discover-sessions-page">
      <div className="mb-8">
        <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-2">Discover Sessions</h1>
        <p className="text-base md:text-lg text-slate-600">Join upcoming group sessions other students have booked — no extra payment once the host accepts.</p>
      </div>

      <Tabs defaultValue="open">
        <TabsList className="mb-6">
          <TabsTrigger value="open" data-testid="tab-open-sessions">Open Sessions</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-my-requests">My Requests ({requests.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="open" className="space-y-6">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input className="pl-9 bg-white" placeholder="Search subject, chapter or teacher" value={q} onChange={(e) => setQ(e.target.value)} data-testid="discover-search-input" />
            </div>
            <Select value={subject} onValueChange={setSubject}>
              <SelectTrigger className="md:w-48 bg-white" data-testid="discover-subject-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All subjects</SelectItem>
                {SUBJECTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="md:w-48 justify-start bg-white" data-testid="discover-date-filter">
                  <CalendarDays className="w-4 h-4 mr-2" />{date ? toIso(date) : 'Any date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().toDateString())} />
              </PopoverContent>
            </Popover>
            {date && (
              <Button variant="ghost" onClick={() => setDate(null)} data-testid="discover-clear-date"><X className="w-4 h-4" /></Button>
            )}
          </div>

          {loading ? (
            <div className="py-16 text-center text-slate-500">Loading sessions...</div>
          ) : sessions.length === 0 ? (
            <Empty text="No open group sessions match right now. Check back soon!" testId="discover-empty" />
          ) : (
            <div className="space-y-4">
              {sessions.map((s) => <SessionCard key={s.id} session={s} onRequest={setTarget} onWithdraw={withdraw} />)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="requests" className="space-y-4">
          {requests.length === 0 ? (
            <Empty text="You haven't requested to join any session yet." testId="my-requests-empty" />
          ) : (
            requests.map((r) => (
              <SessionCard key={r.id} session={r.session} onRequest={setTarget} onWithdraw={withdraw} note={r.message} />
            ))
          )}
        </TabsContent>
      </Tabs>

      <RequestJoinDialog session={target} onClose={() => setTarget(null)} onDone={() => { setTarget(null); load(); }} />
    </div>
  );
};

export default DiscoverSessions;
